/**
 * Green Program — the deterministic engine.
 *
 * Turns metered and entered quantities into tonnes of CO₂e, intensities, an
 * accreditation position, a trajectory against the airport's own target, a
 * standing among peers, a plan of measures, and the anomalies in the meter
 * series. Every factor is visible; every result names the factor it used and
 * says when that factor is a starting value rather than the airport's own.
 * See docs/GREEN-PROGRAM.md.
 *
 * Pure. The page gathers, the engine decides.
 */
import { t, tn } from "../i18n";
import type { Kpi } from "./kpis";

/* ── factors ──────────────────────────────────────────────────────────── */

export interface Factor {
  value: number;
  unit: string;
  source: string;
  /** a starting value the airport has not yet replaced with its own */
  starting: boolean;
}

export interface EmissionFactors {
  grid: Factor; // kgCO₂e per kWh
  diesel: Factor; // kgCO₂e per litre
  petrol: Factor; // kgCO₂e per litre
  solarYield: Factor; // kWh per kWp per year
  wasteLandfill: Factor; // kgCO₂e per tonne to landfill
  waterTreatment: Factor; // kgCO₂e per m³ treated
  apuTurnaround: Factor; // kgCO₂e per turnaround on APU
}

/**
 * Starting factors. None is the airport's own; each says where a real one
 * comes from, and every result computed on one is marked "on a starting
 * factor" until it is replaced.
 */
export function startingFactors(): EmissionFactors {
  return {
    grid: {
      value: 0.6,
      unit: "kgCO₂e/kWh",
      source: "starting value — replace with the supplier's factor or the national inventory",
      starting: true,
    },
    diesel: {
      value: 2.68,
      unit: "kgCO₂e/L",
      source: "starting value — a widely used combustion factor; replace with the national inventory's",
      starting: true,
    },
    petrol: {
      value: 2.31,
      unit: "kgCO₂e/L",
      source: "starting value — a widely used combustion factor; replace with the national inventory's",
      starting: true,
    },
    solarYield: {
      value: 1750,
      unit: "kWh/kWp/yr",
      source: "starting value for the region's irradiance — replace with the array's measured yield",
      starting: true,
    },
    wasteLandfill: {
      value: 450,
      unit: "kgCO₂e/t",
      source: "starting value — replace with the waste contractor's factor",
      starting: true,
    },
    waterTreatment: {
      value: 0.35,
      unit: "kgCO₂e/m³",
      source: "starting value — replace with the utility's factor",
      starting: true,
    },
    apuTurnaround: {
      value: 120,
      unit: "kgCO₂e/turnaround",
      source: "starting value — replace with the fleet mix's APU burn",
      starting: true,
    },
  };
}

/* ── inputs ───────────────────────────────────────────────────────────── */

export interface MonthPoint {
  /** YYYY-MM */
  month: string;
  value: number;
  /** meters that contributed */
  meters: number;
  /** readings across the month — fewer than 25 days' worth is partial */
  readings: number;
}

export interface GreenEntered {
  passengersPerYear?: number;
  dieselLitresPerYear?: number;
  petrolLitresPerYear?: number;
  wasteTonnesPerYear?: number;
  wasteDivertedPct?: number;
  renewableKwhPerYear?: number;
  scope3TonnesEstimate?: number;
  baselineYear?: number;
  baselineTonnes?: number;
  targetYear?: number;
  targetReductionPct?: number;
  netZeroYear?: number;
  lightingSharePct?: number;
  hvacSharePct?: number;
  turnaroundsPerYear?: number;
  contactStands?: number;
  fleetDieselSharePct?: number;
  floorAreaM2?: number;
  enteredBy?: string;
  enteredAt?: string;
}

export interface ChecklistEvidence {
  met: boolean;
  evidence?: string;
  by?: string;
  at?: string;
}

export interface Peer {
  code: string;
  name: string;
  region: "GCC" | "MENA" | "Europe" | "Asia" | "Americas" | "Africa";
  acaLevel?: number;
  kgPerPax?: number;
  year?: number;
  source?: string;
}

export interface Measure {
  key: string;
  label: string;
  costNow: number;
  enabled: boolean;
}

export interface GreenInputs {
  now: Date;
  siteCode: string;
  kpis: Kpi[];
  electricity: MonthPoint[];
  water: MonthPoint[];
  factors: EmissionFactors;
  entered: GreenEntered;
  checklist: Record<string, ChecklistEvidence>;
  peers: Peer[];
  measures: Measure[];
  /** library documents by tag, for the documented requirements */
  documents: { id: string; title: string; tags: string[]; status: string }[];
}

/* ── output ───────────────────────────────────────────────────────────── */

export interface Figure {
  value: number | null;
  unit: string;
  working: string;
  onStartingFactor: boolean;
  sources: string[];
}

export interface Requirement {
  level: number;
  key: string;
  text: string;
  evidence: "computed" | "document" | "entered";
  met: boolean;
  basis: string;
  /** how to close the gap, when not met */
  todo?: string;
}

export interface TrajectoryPoint {
  month: string;
  actualTtm: number | null;
  target: number | null;
}

export interface PlanStep {
  key: string;
  label: string;
  tonnesPerYear: number;
  costNow: number;
  costPerTonne: number;
  working: string;
  onStartingFactor: boolean;
  /** which year of the plan it lands in */
  year: number;
}

export interface Anomaly {
  meter: "electricity" | "water";
  month: string;
  value: number;
  lastYear: number;
  pct: number;
}

export interface GreenOutput {
  generatedAt: string;
  inventory: { scope1: Figure; scope2: Figure; total: Figure; scope3: Figure; months: number; partialMonths: number };
  intensity: { kgPerPax: Figure; kwhPerPax: Figure; kwhPerM2: Figure; litresPerPax: Figure; renewablePct: Figure };
  aca: { level: number; label: string; requirements: Requirement[]; nextLevel?: number; nextTodo: string[] };
  trajectory: {
    points: TrajectoryPoint[];
    state: "on_track" | "behind" | "ahead" | "no_target" | "no_data";
    gapTonnes: number | null;
    gapPct: number | null;
    projectedReachYear: number | null;
    working: string;
  };
  peers: { regional: PeerRank[]; global: PeerRank[]; entered: number; total: number; note: string };
  plan: { gapTonnes: number | null; steps: PlanStep[]; covered: number; note: string };
  anomalies: Anomaly[];
  figures: Set<string>;
  missing: { field: keyof GreenEntered; why: string }[];
}

export interface PeerRank {
  code: string;
  name: string;
  region: Peer["region"];
  kgPerPax?: number;
  acaLevel?: number;
  year?: number;
  source?: string;
  isSelf: boolean;
}

const ACA_LABEL: Record<number, string> = {
  0: "not accredited",
  1: "Level 1 · Mapping",
  2: "Level 2 · Reduction",
  3: "Level 3 · Optimisation",
  3.5: "Level 3+ · Neutrality",
  4: "Level 4 · Transformation",
  4.5: "Level 4+ · Transition",
};

const REQUIREMENTS: Omit<Requirement, "met" | "basis" | "todo">[] = [
  { level: 1, key: "inventory", text: "A Scope 1 and 2 carbon inventory for a full year", evidence: "computed" },
  { level: 1, key: "policy", text: "An environmental or carbon policy, signed", evidence: "document" },
  { level: 1, key: "verification", text: "The inventory verified by a third party", evidence: "entered" },
  { level: 2, key: "target", text: "A reduction target with a baseline year", evidence: "computed" },
  {
    level: 2,
    key: "reduction",
    text: "A reduction in Scope 1 and 2 demonstrated against the baseline",
    evidence: "computed",
  },
  { level: 2, key: "management_plan", text: "A carbon management plan", evidence: "document" },
  {
    level: 3,
    key: "scope3",
    text: "Scope 3 emissions mapped — tenants, airlines, surface access, aircraft LTO",
    evidence: "entered",
  },
  {
    level: 3,
    key: "stakeholder",
    text: "A stakeholder engagement plan with airlines and tenants",
    evidence: "document",
  },
  {
    level: 3.5,
    key: "offsets",
    text: "Residual Scope 1 and 2 offset with retired credits on a recognized standard",
    evidence: "entered",
  },
  {
    level: 4,
    key: "absolute",
    text: "Absolute reduction on a science-based pathway to net zero",
    evidence: "computed",
  },
  {
    level: 4,
    key: "partnership",
    text: "A stakeholder partnership plan with commitments from third parties",
    evidence: "document",
  },
  { level: 4.5, key: "compensation", text: "Compensation of residual emissions under Level 4+", evidence: "entered" },
];

const DOC_TAGS: Record<string, string[]> = {
  policy: ["environmental-policy", "carbon-policy", "environment", "sustainability-policy"],
  management_plan: ["carbon-management-plan", "carbon-plan", "sustainability-plan"],
  stakeholder: ["stakeholder-engagement", "stakeholder-plan"],
  partnership: ["stakeholder-partnership", "partnership-plan"],
};

/* ── helpers ──────────────────────────────────────────────────────────── */

/** The trajectory state as the screen reads it: the English is the state with its underscore made a space. */
export function trajectoryStateLabel(state: GreenOutput["trajectory"]["state"]): string {
  switch (state) {
    case "on_track":
      return t("on track");
    case "behind":
      return t("behind");
    case "ahead":
      return t("ahead");
    case "no_target":
      return t("no target");
    case "no_data":
      return t("no data");
  }
}

const r1 = (n: number) => Math.round(n * 10) / 10;
const fig = (
  value: number | null,
  unit: string,
  working: string,
  onStartingFactor: boolean,
  sources: string[],
): Figure => ({ value: value == null ? null : r1(value), unit, working, onStartingFactor, sources });

/** the trailing twelve months ending at the latest month, and whether any is partial */
function trailing(series: MonthPoint[]): { months: MonthPoint[]; partial: number } {
  const sorted = [...series].sort((a, b) => a.month.localeCompare(b.month));
  const months = sorted.slice(-12);
  return { months, partial: months.filter((m) => m.readings > 0 && m.readings < 25).length };
}

function slope(points: { x: number; y: number }[]): number | null {
  if (points.length < 3) return null;
  const mx = points.reduce((t, p) => t + p.x, 0) / points.length;
  const my = points.reduce((t, p) => t + p.y, 0) / points.length;
  const sxx = points.reduce((t, p) => t + (p.x - mx) ** 2, 0);
  if (sxx === 0) return null;
  return points.reduce((t, p) => t + (p.x - mx) * (p.y - my), 0) / sxx;
}

/* ── the engine ───────────────────────────────────────────────────────── */

export function runGreen(inp: GreenInputs): GreenOutput {
  const f = inp.factors;
  const e = inp.entered;
  const missing: GreenOutput["missing"] = [];
  const kpi = (key: string) => inp.kpis.find((k) => k.key === key);

  /* inventory */
  const el = trailing(inp.electricity);
  const kwh = el.months.reduce((t, m) => t + m.value, 0);
  const months = el.months.length;
  const kwhAnnual = months > 0 && months < 12 ? (kwh / months) * 12 : kwh;
  const renewableKwh = e.renewableKwhPerYear ?? 0;
  const scope2t = months > 0 ? ((kwhAnnual - renewableKwh) * f.grid.value) / 1000 : null;
  const scope2 = fig(
    scope2t,
    "tCO₂e/yr",
    months > 0
      ? t("{kwh} kWh over {months}{annualised}{renewable} × {factor} {unit} ÷ 1000", {
          kwh: Math.round(kwh).toLocaleString("en"),
          months: tn(months, "{n} metered month", "{n} metered months"),
          annualised:
            months < 12 ? t(", annualised to {kwh}", { kwh: Math.round(kwhAnnual).toLocaleString("en") }) : "",
          renewable: renewableKwh
            ? t(", less {kwh} kWh on-site renewable", { kwh: renewableKwh.toLocaleString("en") })
            : "",
          factor: f.grid.value,
          unit: f.grid.unit,
        })
      : t("no metered electricity on the platform"),
    f.grid.starting,
    [`${el.months.reduce((t, m) => Math.max(t, m.meters), 0)} electricity meters`, `grid factor: ${f.grid.source}`],
  );
  const diesel = e.dieselLitresPerYear ?? 0;
  const petrol = e.petrolLitresPerYear ?? 0;
  const scope1t =
    e.dieselLitresPerYear != null || e.petrolLitresPerYear != null
      ? (diesel * f.diesel.value + petrol * f.petrol.value) / 1000
      : null;
  if (scope1t == null)
    missing.push({
      field: "dieselLitresPerYear",
      why: t(
        "no ground fuel on the platform; enter diesel and petrol litres per year for the fleet and generators — Scope 1 is blank until then",
      ),
    });
  const scope1 = fig(
    scope1t,
    "tCO₂e/yr",
    scope1t != null
      ? t("{diesel} L diesel × {dieselFactor} + {petrol} L petrol × {petrolFactor}, ÷ 1000", {
          diesel: diesel.toLocaleString("en"),
          dieselFactor: f.diesel.value,
          petrol: petrol.toLocaleString("en"),
          petrolFactor: f.petrol.value,
        })
      : t("no fuel entered"),
    f.diesel.starting || f.petrol.starting,
    ["entered fuel", `factors: ${f.diesel.source}`],
  );
  const totalT = scope2t != null ? scope2t + (scope1t ?? 0) : null;
  const total = fig(
    totalT,
    "tCO₂e/yr",
    scope1t == null ? t("Scope 2 only — Scope 1 not entered") : t("Scope 1 + Scope 2"),
    scope2.onStartingFactor || scope1.onStartingFactor,
    [...scope2.sources, ...scope1.sources],
  );
  const scope3 = fig(
    e.scope3TonnesEstimate ?? null,
    "tCO₂e/yr",
    e.scope3TonnesEstimate != null ? t("entered estimate — never added to the total") : t("not mapped"),
    false,
    ["entered"],
  );

  /* intensities */
  const pax = e.passengersPerYear;
  if (pax == null)
    missing.push({
      field: "passengersPerYear",
      why: t("no passenger count on the platform; every per-passenger intensity is blank until it is entered"),
    });
  const wt = trailing(inp.water);
  const m3 = wt.months.reduce((t, m) => t + m.value, 0);
  const m3Annual = wt.months.length > 0 && wt.months.length < 12 ? (m3 / wt.months.length) * 12 : m3;
  const intensity: GreenOutput["intensity"] = {
    kgPerPax: fig(
      totalT != null && pax ? (totalT * 1000) / pax : null,
      "kgCO₂e/pax",
      totalT != null && pax
        ? t("{tonnes} t × 1000 ÷ {pax} passengers", { tonnes: r1(totalT), pax: pax.toLocaleString("en") })
        : t("needs the inventory and passengers"),
      total.onStartingFactor,
      total.sources,
    ),
    kwhPerPax: fig(
      months > 0 && pax ? kwhAnnual / pax : null,
      "kWh/pax",
      months > 0 && pax
        ? t("{kwh} kWh ÷ {pax}", { kwh: Math.round(kwhAnnual).toLocaleString("en"), pax: pax.toLocaleString("en") })
        : t("needs metered electricity and passengers"),
      false,
      ["meters", "entered passengers"],
    ),
    kwhPerM2: fig(
      (kpi("energy_intensity_kwh_m2") ?? kpi("intensity_kwh_m2"))?.value ??
        (months > 0 && e.floorAreaM2 ? kwhAnnual / e.floorAreaM2 : null),
      "kWh/m²",
      (kpi("energy_intensity_kwh_m2") ?? kpi("intensity_kwh_m2"))
        ? t("the platform's own KPI")
        : e.floorAreaM2
          ? t("annual kWh ÷ entered floor area")
          : t("no floor area"),
      false,
      [
        (kpi("energy_intensity_kwh_m2") ?? kpi("intensity_kwh_m2"))
          ? "KPI energy_intensity_kwh_m2"
          : "entered floor area",
      ],
    ),
    litresPerPax: fig(
      wt.months.length > 0 && pax ? (m3Annual * 1000) / pax : null,
      "L/pax",
      wt.months.length > 0 && pax
        ? t("{m3} m³ × 1000 ÷ {pax}", { m3: Math.round(m3Annual).toLocaleString("en"), pax: pax.toLocaleString("en") })
        : t("needs metered water and passengers"),
      false,
      ["water meters", "entered passengers"],
    ),
    renewablePct: fig(
      kpi("renewable_fraction_pct")?.value ?? (months > 0 && renewableKwh ? (renewableKwh / kwhAnnual) * 100 : null),
      "%",
      kpi("renewable_fraction_pct")
        ? t("the platform's own KPI from the solar arrays")
        : renewableKwh
          ? t("entered generation ÷ annual electricity")
          : t("no solar metered and none entered"),
      false,
      [kpi("renewable_fraction_pct") ? "KPI renewable_fraction_pct" : "entered"],
    ),
  };

  /* trajectory */
  const sorted = [...inp.electricity].sort((a, b) => a.month.localeCompare(b.month));
  const ttm: { month: string; tonnes: number }[] = [];
  for (let i = 11; i < sorted.length; i++) {
    const window = sorted.slice(i - 11, i + 1);
    ttm.push({
      month: sorted[i].month,
      tonnes: ((window.reduce((t, m) => t + m.value, 0) - renewableKwh) * f.grid.value) / 1000 + (scope1t ?? 0),
    });
  }
  const hasTarget =
    e.baselineYear != null && e.baselineTonnes != null && e.targetYear != null && e.targetReductionPct != null;
  if (!hasTarget)
    missing.push({
      field: "targetReductionPct",
      why: t(
        "no baseline year, baseline tonnes, target year or reduction target entered; the trajectory and the plan need all four",
      ),
    });
  const targetAt = (month: string): number | null => {
    if (!hasTarget) return null;
    const y = Number(month.slice(0, 4)) + (Number(month.slice(5, 7)) - 1) / 12;
    const b = e.baselineYear!;
    const t = e.targetYear!;
    if (t <= b) return e.baselineTonnes!;
    const frac = Math.min(1, Math.max(0, (y - b) / (t - b)));
    return e.baselineTonnes! * (1 - (e.targetReductionPct! / 100) * frac);
  };
  const points: TrajectoryPoint[] = sorted.map((m) => ({
    month: m.month,
    actualTtm: ttm.find((x) => x.month === m.month)?.tonnes ?? null,
    target: targetAt(m.month),
  }));
  let state: GreenOutput["trajectory"]["state"] = "no_data";
  let gapTonnes: number | null = null;
  let gapPct: number | null = null;
  let reach: number | null = null;
  let working = t("fewer than twelve metered months: no trailing-twelve-month total yet");
  const last = ttm[ttm.length - 1];
  if (last && !hasTarget) {
    state = "no_target";
    working = t("trailing twelve months {tonnes} t; no target entered to compare against", { tonnes: r1(last.tonnes) });
  } else if (last && hasTarget) {
    const tgt = targetAt(last.month)!;
    gapTonnes = r1(last.tonnes - tgt);
    gapPct = tgt > 0 ? r1(((last.tonnes - tgt) / tgt) * 100) : null;
    state = gapTonnes > tgt * 0.02 ? "behind" : gapTonnes < -tgt * 0.02 ? "ahead" : "on_track";
    const sl = slope(ttm.map((p, i) => ({ x: i, y: p.tonnes })));
    const endTarget = e.baselineTonnes! * (1 - e.targetReductionPct! / 100);
    if (sl != null && sl < 0) {
      const monthsToReach = (last.tonnes - endTarget) / -sl;
      reach = Number(last.month.slice(0, 4)) + Math.ceil(monthsToReach + Number(last.month.slice(5, 7))) / 12;
      reach = Math.round(reach * 10) / 10;
    }
    const vars = {
      tonnes: r1(last.tonnes),
      target: r1(tgt),
      baseline: `${e.baselineTonnes}`,
      baselineYear: `${e.baselineYear}`,
      pct: `${e.targetReductionPct}`,
      targetYear: `${e.targetYear}`,
      trend: sl != null ? r1(sl * 12) : "",
      reach: `${reach}`,
    };
    working =
      sl == null
        ? t(
            "trailing twelve months {tonnes} t against the target line's {target} t this month (baseline {baseline} t in {baselineYear}, −{pct}% by {targetYear}); least-squares trend not computable",
            vars,
          )
        : reach != null
          ? t(
              "trailing twelve months {tonnes} t against the target line's {target} t this month (baseline {baseline} t in {baselineYear}, −{pct}% by {targetYear}); least-squares trend {trend} t/yr, reaching the target around {reach}",
              vars,
            )
          : sl >= 0
            ? t(
                "trailing twelve months {tonnes} t against the target line's {target} t this month (baseline {baseline} t in {baselineYear}, −{pct}% by {targetYear}); least-squares trend {trend} t/yr, which never reaches it",
                vars,
              )
            : t(
                "trailing twelve months {tonnes} t against the target line's {target} t this month (baseline {baseline} t in {baselineYear}, −{pct}% by {targetYear}); least-squares trend {trend} t/yr",
                vars,
              );
  }

  /* ACA checklist */
  const docsFor = (key: string) =>
    inp.documents.filter((d) => d.tags.some((t) => (DOC_TAGS[key] ?? []).includes(t)) && d.status !== "withdrawn");
  const requirements: Requirement[] = REQUIREMENTS.map((r) => {
    const ck = inp.checklist[r.key];
    if (r.evidence === "computed") {
      if (r.key === "inventory")
        return {
          ...r,
          met: months >= 12 && scope1t != null,
          basis:
            months >= 12 && scope1t != null
              ? t("{months} metered months and fuel entered", { months })
              : scope1t == null
                ? t("{months} of 12 metered months, fuel not entered", { months })
                : t("{months} of 12 metered months", { months }),
          todo:
            months < 12
              ? t("connect or backfill the electricity meters to twelve months")
              : scope1t == null
                ? t("enter the year's fuel")
                : undefined,
        };
      if (r.key === "target")
        return {
          ...r,
          met: hasTarget,
          basis: hasTarget
            ? t("baseline {baselineYear}, −{pct}% by {targetYear}", {
                baselineYear: `${e.baselineYear}`,
                pct: `${e.targetReductionPct}`,
                targetYear: `${e.targetYear}`,
              })
            : t("no target entered"),
          todo: hasTarget ? undefined : t("enter the baseline year and tonnes, the target year and the reduction"),
        };
      if (r.key === "reduction")
        return {
          ...r,
          met: hasTarget && last != null && last.tonnes < e.baselineTonnes!,
          basis:
            hasTarget && last
              ? t("{tonnes} t trailing against a baseline of {baseline} t", {
                  tonnes: r1(last.tonnes),
                  baseline: `${e.baselineTonnes}`,
                })
              : t("needs the target and twelve metered months"),
          todo:
            hasTarget && last && last.tonnes >= e.baselineTonnes!
              ? t("the trailing year is not below the baseline yet — see the plan")
              : undefined,
        };
      if (r.key === "absolute")
        return {
          ...r,
          met: state === "ahead" || (state === "on_track" && (e.targetReductionPct ?? 0) >= 30),
          basis:
            state === "no_data" || state === "no_target"
              ? t("needs the trajectory")
              : t("{state} on a {pct}% target", {
                  state: trajectoryStateLabel(state),
                  pct: `${e.targetReductionPct ?? "—"}`,
                }),
          todo: t("a science-based pathway needs at least a 30% reduction target and the trajectory on or ahead of it"),
        };
    }
    if (r.evidence === "document") {
      const docs = docsFor(r.key);
      return {
        ...r,
        met: docs.length > 0 || !!ck?.met,
        basis: docs.length
          ? t("in the library: {titles}", { titles: docs.map((d) => d.title).join("; ") })
          : ck?.met
            ? `${t("entered as met")}${ck.evidence ? `: ${ck.evidence}` : ""}${ck.by ? ` (${ck.by})` : ""}`
            : t("no document tagged {tags} in the library", { tags: (DOC_TAGS[r.key] ?? []).join(t(" or ")) }),
        todo:
          docs.length || ck?.met
            ? undefined
            : t("add the document to the library with the tag, or record where it is held"),
      };
    }
    return {
      ...r,
      met: !!ck?.met,
      basis: ck?.met
        ? `${t("entered as met")}${ck.evidence ? `: ${ck.evidence}` : ""}${ck.by ? ` (${ck.by}, ${ck.at?.slice(0, 10) ?? ""})` : ""}`
        : t("not recorded"),
      todo: ck?.met
        ? undefined
        : t("record the evidence — the verifier and date, the Scope 3 map, the retired credits"),
    };
  });
  const levels = [1, 2, 3, 3.5, 4, 4.5];
  let level = 0;
  for (const l of levels) {
    if (requirements.filter((r) => r.level <= l).every((r) => r.met)) level = l;
    else break;
  }
  const nextLevel = levels.find((l) => l > level);
  const nextTodo = requirements
    .filter((r) => r.level <= (nextLevel ?? 0) && !r.met)
    .map((r) => `${r.text}: ${r.todo ?? r.basis}`);

  /* peers */
  const selfKg = intensity.kgPerPax.value;
  const rank = (list: Peer[]): PeerRank[] => {
    const rows: PeerRank[] = list.map((p) => ({ ...p, isSelf: false }));
    rows.push({
      code: inp.siteCode,
      name: t("this airport"),
      region: "GCC",
      kgPerPax: selfKg ?? undefined,
      acaLevel: level,
      year: inp.now.getFullYear(),
      isSelf: true,
    });
    return rows.sort((a, b) => (a.kgPerPax ?? Infinity) - (b.kgPerPax ?? Infinity));
  };
  const withValues = inp.peers.filter((p) => p.kgPerPax != null || p.acaLevel != null).length;
  const peers = {
    regional: rank(inp.peers.filter((p) => p.region === "GCC" || p.region === "MENA")),
    global: rank(inp.peers),
    entered: withValues,
    total: inp.peers.length,
    note:
      withValues === 0
        ? t(
            "No peer has values yet. Enter each airport's ACA level and kgCO₂e per passenger from the ACA participant registry and its published sustainability report, with the year and the source; the ranking appears as values arrive.",
          )
        : t("{n} of {total} peers have values entered; the rest are shown blank, not estimated.", {
            n: withValues,
            total: inp.peers.length,
          }),
  };

  /* plan */
  const steps: PlanStep[] = [];
  const kwhBase = months > 0 ? kwhAnnual : 0;
  const measure = (key: string) => inp.measures.find((m) => m.key === key);
  const push = (key: string, label: string, tonnes: number | null, working: string, starting: boolean) => {
    const m = measure(key);
    if (!m || !m.enabled || tonnes == null || tonnes <= 0) return;
    steps.push({
      key,
      label,
      tonnesPerYear: r1(tonnes),
      costNow: m.costNow,
      costPerTonne: Math.round(m.costNow / tonnes),
      working,
      onStartingFactor: starting,
      year: 0,
    });
  };
  push(
    "led",
    t("LED relamping"),
    e.lightingSharePct != null && kwhBase ? ((kwhBase * e.lightingSharePct) / 100) * 0.5 * (f.grid.value / 1000) : null,
    e.lightingSharePct != null
      ? t("{pct}% lighting share × {kwh} kWh × 0.5 saved × grid factor", {
          pct: e.lightingSharePct,
          kwh: Math.round(kwhBase).toLocaleString("en"),
        })
      : t("needs the lighting share of electricity"),
    f.grid.starting,
  );
  const kwp = measure("solar")?.costNow ? Math.round(measure("solar")!.costNow / 3500) : 0;
  push(
    "solar",
    t("Solar PV expansion ({kwp} kWp at the card's rate)", { kwp }),
    kwp ? (kwp * f.solarYield.value * f.grid.value) / 1000 : null,
    t("{kwp} kWp × {yield} {unit} × grid factor", { kwp, yield: f.solarYield.value, unit: f.solarYield.unit }),
    f.solarYield.starting || f.grid.starting,
  );
  push(
    "hvac",
    t("HVAC setpoints and scheduling"),
    e.hvacSharePct != null && kwhBase ? ((kwhBase * e.hvacSharePct) / 100) * 0.12 * (f.grid.value / 1000) : null,
    e.hvacSharePct != null
      ? t("{pct}% HVAC share × annual kWh × 0.12 saved × grid factor", { pct: e.hvacSharePct })
      : t("needs the HVAC share of electricity"),
    f.grid.starting,
  );
  push(
    "gpu",
    t("GPU and PCA at contact stands in place of APU"),
    e.turnaroundsPerYear != null ? (e.turnaroundsPerYear * 0.6 * f.apuTurnaround.value) / 1000 : null,
    e.turnaroundsPerYear != null
      ? t("{turnarounds} turnarounds × 60% on contact stands × {kg} kg", {
          turnarounds: e.turnaroundsPerYear.toLocaleString("en"),
          kg: f.apuTurnaround.value,
        })
      : t("needs turnarounds per year"),
    f.apuTurnaround.starting,
  );
  push(
    "fleet",
    t("Fleet electrification"),
    e.fleetDieselSharePct != null && diesel
      ? (((diesel * e.fleetDieselSharePct) / 100) * (f.diesel.value - 0.3 * f.grid.value)) / 1000
      : null,
    e.fleetDieselSharePct != null
      ? t("{pct}% of {diesel} L diesel × (diesel factor − replacement electricity)", {
          pct: e.fleetDieselSharePct,
          diesel: diesel.toLocaleString("en"),
        })
      : t("needs the fleet's diesel share and the year's fuel"),
    f.diesel.starting,
  );
  push(
    "waste",
    t("Waste diversion to 60%"),
    e.wasteTonnesPerYear != null
      ? ((e.wasteTonnesPerYear * Math.max(0, 60 - (e.wasteDivertedPct ?? 0))) / 100) * (f.wasteLandfill.value / 1000)
      : null,
    e.wasteTonnesPerYear != null
      ? t("{tonnes} t × (60% − {pct}% diverted) × landfill factor", {
          tonnes: e.wasteTonnesPerYear,
          pct: e.wasteDivertedPct ?? 0,
        })
      : t("needs waste tonnes and the diverted share"),
    f.wasteLandfill.starting,
  );
  push(
    "water",
    t("Water recycling of 25% of consumption"),
    wt.months.length ? m3Annual * 0.25 * (f.waterTreatment.value / 1000) : null,
    t("{m3} m³ × 25% × treatment factor", { m3: Math.round(m3Annual).toLocaleString("en") }),
    f.waterTreatment.starting,
  );
  steps.sort((a, b) => a.costPerTonne - b.costPerTonne);
  /* the gap the plan has to close is always the distance to the end target, whatever this month's line says */
  const planGap =
    hasTarget && last ? Math.max(0, r1(last.tonnes - e.baselineTonnes! * (1 - e.targetReductionPct! / 100))) : null;
  let covered = 0;
  const yearsLeft = hasTarget ? Math.max(1, e.targetYear! - inp.now.getFullYear()) : 3;
  steps.forEach((s, i) => {
    s.year = inp.now.getFullYear() + Math.min(yearsLeft - 1, Math.floor((i / Math.max(1, steps.length)) * yearsLeft));
    if (planGap != null && covered < planGap) covered += s.tonnesPerYear;
  });
  const plan = {
    gapTonnes: planGap,
    steps,
    covered: r1(covered),
    note:
      planGap == null
        ? t("No target to plan against yet.")
        : covered >= planGap
          ? t("The measures cover the {gap} t gap to the target, cheapest per tonne first.", { gap: planGap })
          : t(
              "The measures cover {covered} of the {gap} t gap; the rest needs measures not in the catalog, or a Scope 1 or grid change.",
              { covered: r1(covered), gap: planGap },
            ),
  };

  /* anomalies */
  const anomalies: Anomaly[] = [];
  for (const [meter, series] of [
    ["electricity", inp.electricity],
    ["water", inp.water],
  ] as const) {
    const byMonth = new Map(series.map((m) => [m.month, m.value]));
    for (const m of series) {
      const ly = `${Number(m.month.slice(0, 4)) - 1}${m.month.slice(4)}`;
      const prev = byMonth.get(ly);
      if (prev && prev > 0 && m.value > prev * 1.15)
        anomalies.push({
          meter,
          month: m.month,
          value: m.value,
          lastYear: prev,
          pct: Math.round(((m.value - prev) / prev) * 100),
        });
    }
  }
  anomalies.sort((a, b) => b.month.localeCompare(a.month));

  const figures = new Set<string>();
  const add = (n: number | null | undefined) => {
    if (n == null || Number.isNaN(n)) return;
    figures.add(String(Math.round(n)));
    figures.add(String(r1(n)));
    figures.add(Math.round(n).toLocaleString("en"));
  };
  for (const x of [scope1, scope2, total, scope3, ...Object.values(intensity)]) add(x.value);
  add(gapTonnes);
  add(gapPct);
  add(reach);
  add(level);
  add(months);
  add(pax);
  add(planGap);
  add(covered);
  for (const s of steps) {
    add(s.tonnesPerYear);
    add(s.costNow);
    add(s.costPerTonne);
    add(s.year);
  }
  for (const a of anomalies) {
    add(a.value);
    add(a.lastYear);
    add(a.pct);
  }
  for (const p of inp.peers) {
    add(p.kgPerPax);
    add(p.acaLevel);
    add(p.year);
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));
  figures.add(String(inp.now.getFullYear()));
  /* every number in the material the model sees, and every requirement level */
  const draft: GreenOutput = {
    generatedAt: inp.now.toISOString(),
    inventory: { scope1, scope2, total, scope3, months, partialMonths: el.partial },
    intensity,
    aca: { level, label: ACA_LABEL[level] ?? `Level ${level}`, requirements, nextLevel, nextTodo },
    trajectory: { points, state, gapTonnes, gapPct, projectedReachYear: reach, working },
    peers,
    plan,
    anomalies,
    figures,
    missing,
  };
  for (const n of materialForGreenModel(draft, inp.siteCode).match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }

  return {
    generatedAt: inp.now.toISOString(),
    inventory: { scope1, scope2, total, scope3, months, partialMonths: el.partial },
    intensity,
    aca: { level, label: ACA_LABEL[level] ?? `Level ${level}`, requirements, nextLevel, nextTodo },
    trajectory: { points, state, gapTonnes, gapPct, projectedReachYear: reach, working },
    peers,
    plan,
    anomalies,
    figures,
    missing,
  };
}

/** The peer set, names only: levels and intensities are entered from published sources. */
export function defaultPeers(): Peer[] {
  return [
    { code: "OERK", name: "Riyadh King Khalid", region: "GCC" },
    { code: "OEJN", name: "Jeddah King Abdulaziz", region: "GCC" },
    { code: "OEDF", name: "Dammam King Fahd", region: "GCC" },
    { code: "OMDB", name: "Dubai International", region: "GCC" },
    { code: "OMAA", name: "Abu Dhabi Zayed", region: "GCC" },
    { code: "OTHH", name: "Doha Hamad", region: "GCC" },
    { code: "OOMS", name: "Muscat", region: "GCC" },
    { code: "HECA", name: "Cairo", region: "MENA" },
    { code: "EHAM", name: "Amsterdam Schiphol", region: "Europe" },
    { code: "ENGM", name: "Oslo Gardermoen", region: "Europe" },
    { code: "LEMD", name: "Madrid Barajas", region: "Europe" },
    { code: "WSSS", name: "Singapore Changi", region: "Asia" },
    { code: "VIDP", name: "Delhi Indira Gandhi", region: "Asia" },
    { code: "KDEN", name: "Denver", region: "Americas" },
    { code: "FACT", name: "Cape Town", region: "Africa" },
  ];
}

export function defaultMeasures(): Measure[] {
  return [
    { key: "led", label: "LED relamping", costNow: 1_800_000, enabled: true },
    { key: "solar", label: "Solar PV expansion", costNow: 7_000_000, enabled: true },
    { key: "hvac", label: "HVAC setpoints and scheduling", costNow: 400_000, enabled: true },
    { key: "gpu", label: "GPU and PCA at contact stands", costNow: 3_500_000, enabled: true },
    { key: "fleet", label: "Fleet electrification", costNow: 5_000_000, enabled: true },
    { key: "waste", label: "Waste diversion", costNow: 600_000, enabled: true },
    { key: "water", label: "Water recycling", costNow: 2_200_000, enabled: true },
  ];
}

export const GREEN_MODEL_PROMPT = [
  "You are explaining an airport's carbon programme to its chief executive and, when asked, drafting text for its board or the regulator.",
  "You may explain, compare, challenge and draft. You must not state any number that is not in the material you are given; do not estimate or extrapolate.",
  "Say plainly when a figure rests on a starting factor rather than the airport's own, and when a comparison is against a blank.",
].join(" ");

export function materialForGreenModel(o: GreenOutput, siteCode: string): string {
  const L: string[] = [
    `Airport ${siteCode}. ACA position: ${o.aca.label}. Trajectory: ${o.trajectory.state.replace("_", " ")} — ${o.trajectory.working}.`,
  ];
  L.push(
    `Inventory: Scope 1 ${o.inventory.scope1.value ?? "n/a"} t (${o.inventory.scope1.working}); Scope 2 ${o.inventory.scope2.value ?? "n/a"} t (${o.inventory.scope2.working}); total ${o.inventory.total.value ?? "n/a"} t; Scope 3 ${o.inventory.scope3.value ?? "n/a"} t entered. ${o.inventory.total.onStartingFactor ? "On starting factors." : ""}`,
  );
  for (const [k, v] of Object.entries(o.intensity)) L.push(`${k}: ${v.value ?? "n/a"} ${v.unit} — ${v.working}`);
  for (const r of o.aca.requirements) L.push(`ACA L${r.level} ${r.text}: ${r.met ? "met" : "not met"} — ${r.basis}`);
  L.push(
    `Peers: ${o.peers.note} ${o.peers.global
      .filter((p) => p.kgPerPax != null)
      .map((p) => `${p.name} ${p.kgPerPax} kg/pax (${p.year ?? "?"})`)
      .join("; ")}`,
  );
  L.push(
    `Plan: ${o.plan.note} ${o.plan.steps.map((s) => `${s.label}: ${s.tonnesPerYear} t/yr for ${s.costNow} (${s.costPerTonne}/t) in ${s.year}`).join("; ")}`,
  );
  if (o.anomalies.length)
    L.push(
      `Anomalies: ${o.anomalies.map((a) => `${a.meter} ${a.month} ${a.value} vs ${a.lastYear} last year (+${a.pct}%)`).join("; ")}`,
    );
  return L.join("\n");
}
